import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import { fetchUserSubscriptions } from "../../services/userSubscriptionService";
import { PLAN_TYPE_META, PLAN_TYPE_ORDER } from "../../../common/constants";

const ACCENT = {
  GROUP:            "#6f42c1",
  ONE_ON_ONE:       "#007BFF",
  PARTNER_PRACTICE: "#e91e8c",
  COMBO:            "#fd7e14",
};

const LIGHT = {
  GROUP:            "#f3eeff",
  ONE_ON_ONE:       "#e8f2ff",
  PARTNER_PRACTICE: "#fde8f4",
  COMBO:            "#fff4e8",
};

const ICON = {
  GROUP:            "👥",
  ONE_ON_ONE:       "👤",
  PARTNER_PRACTICE: "🤝",
  COMBO:            "📦",
};

const effectiveType = (sub) => sub.subPlanType || sub.planType;

const fmt = (dateStr) =>
  dateStr
    ? new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : null;

const MyPlansPage = () => {
  const [userPlans, setUserPlans] = useState([]);
  const [loading, setLoading]     = useState(true);
  const navigate = useNavigate();

  const fetchUserPlans = useCallback(async () => {
    try {
      const plans = await fetchUserSubscriptions();
      setUserPlans(plans);
    } catch (error) {
      if (error.response?.status === 401) navigate("/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchUserPlans(); }, [fetchUserPlans]);

  const summaryTypes = ["GROUP", "ONE_ON_ONE", "PARTNER_PRACTICE"];
  const totalRemaining = userPlans.reduce((sum, p) => sum + (p.remainingSessions || 0), 0);
  const remainingByEffectiveType = (type) =>
    userPlans.filter((p) => effectiveType(p) === type)
             .reduce((sum, p) => sum + (p.remainingSessions || 0), 0);

  const grouped = PLAN_TYPE_ORDER.reduce((acc, type) => {
    const plans = userPlans.filter((p) => effectiveType(p) === type);
    if (plans.length) acc[type] = plans;
    return acc;
  }, {});

  const sortedPlans = (plans) => [
    ...plans.filter((p) => (p.remainingSessions || 0) > 0),
    ...plans.filter((p) => (p.remainingSessions || 0) <= 0),
  ];

  if (loading) return (
    <>
      <Navbar />
      <div style={s.loadingWrap}>
        <div style={s.spinner} />
        <p style={s.loadingText}>Loading your plans...</p>
      </div>
    </>
  );

  return (
    <div style={s.page}>
      <Navbar />

      {/* ── PAGE HEADER ── */}
      <div style={s.pageHeader}>
        <div style={s.headerInner}>
          <div>
            <h1 style={s.pageTitle}>My Plans</h1>
            <p style={s.pageSubtitle}>Track your sessions and manage your subscriptions</p>
          </div>
          <div style={s.btnWrap}>
            <button style={s.historyBtn} onClick={() => navigate("/transaction-history")}>
              🧾 Transaction History
            </button>
            <button style={s.purchaseBtn} onClick={() => navigate("/plans")}>
              + Purchase New Plan
            </button>
          </div>
        </div>
      </div>

      <div style={s.container}>
        {userPlans.length === 0 ? (
          <div style={s.emptyState}>
            <div style={s.emptyIcon}>📋</div>
            <h3 style={s.emptyTitle}>No plans yet</h3>
            <p style={s.emptyText}>Purchase a plan to start booking sessions.</p>
            <button style={s.purchaseBtn} onClick={() => navigate("/plans")}>
              Browse Plans
            </button>
          </div>
        ) : (
          <>
            {/* ── SUMMARY BAR ── */}
            <div style={s.summaryBar}>
              <div style={s.summaryTitle}>Sessions Remaining</div>
              <div style={s.summaryChips}>
                <SummaryChip label="Total" value={totalRemaining} color="#028891" icon="📊" bg="#e8f7f8" />
                {summaryTypes
                  .filter((type) => userPlans.some((p) => effectiveType(p) === type))
                  .map((type) => (
                    <SummaryChip
                      key={type}
                      label={PLAN_TYPE_META[type].displayName}
                      value={remainingByEffectiveType(type)}
                      color={ACCENT[type]}
                      icon={ICON[type]}
                      bg={LIGHT[type]}
                    />
                  ))}
              </div>
            </div>

            {/* ── GROUPED SECTIONS ── */}
            {Object.entries(grouped).map(([type, plans]) => (
              <div key={type} style={s.section}>
                {/* Section header */}
                <div style={s.sectionHeader}>
                  <div style={{ ...s.sectionIcon, background: LIGHT[type], color: ACCENT[type] }}>
                    {ICON[type]}
                  </div>
                  <div>
                    <div style={{ ...s.sectionTitle, color: ACCENT[type] }}>
                      {PLAN_TYPE_META[type].displayName}
                    </div>
                    <div style={s.sectionDesc}>{PLAN_TYPE_META[type].description}</div>
                  </div>
                  <div style={{ ...s.sectionCount, background: LIGHT[type], color: ACCENT[type] }}>
                    {plans.length} plan{plans.length !== 1 ? "s" : ""}
                  </div>
                </div>

                {/* Cards grid */}
                <div style={s.grid}>
                  {sortedPlans(plans).map((sub) => {
                    const isActive   = sub.status === "ACTIVE";
                    const isExpired  = (sub.remainingSessions || 0) <= 0;
                    const progress   = sub.totalSessions > 0
                      ? Math.round((sub.remainingSessions / sub.totalSessions) * 100)
                      : 0;
                    const accent     = ACCENT[type];
                    const light      = LIGHT[type];
                    const isComboRow = sub.planType === "COMBO";

                    return (
                      <div key={sub.id} style={{
                        ...s.card,
                        ...(isExpired ? s.cardExpired : {}),
                        ...(isActive && !isExpired ? { boxShadow: `0 4px 20px ${accent}22` } : {}),
                      }}>
                        {/* Card top accent bar */}
                        <div style={{ ...s.cardAccentBar, background: isExpired ? "#e0e0e0" : accent }} />

                        {/* Expired overlay label */}
                        {isExpired && (
                          <div style={s.expiredBanner}>✕ Expired</div>
                        )}

                        {/* Card header row */}
                        <div style={s.cardHeader}>
                          <div style={{ ...s.typeIcon, background: isExpired ? "#f0f0f0" : light, color: isExpired ? "#bbb" : accent }}>
                            {ICON[type]}
                          </div>
                          {isActive && !isExpired && (
                            <span style={{ ...s.activePill, background: accent }}>● Active</span>
                          )}
                        </div>

                        {/* Plan name */}
                        <div style={{ ...s.planName, color: isExpired ? "#aaa" : "#1a1a1a" }}>
                          {sub.planName}
                        </div>

                        {/* Combo source tag */}
                        {isComboRow && (
                          <span style={{ ...s.comboTag, borderColor: accent, color: accent, background: light }}>
                            🎯 From Combo Plan
                          </span>
                        )}

                        {/* ── Sessions block ── */}
                        <div style={{ ...s.sessionsBlock, background: isExpired ? "#fafafa" : light }}>
                          <div style={s.sessionMain}>
                            <span style={{ ...s.sessionBig, color: isExpired ? "#bbb" : accent }}>
                              {sub.remainingSessions}
                            </span>
                            <span style={s.sessionOf}>/ {sub.totalSessions}</span>
                            <span style={s.sessionLabel}>sessions left</span>
                          </div>

                          {/* Progress bar */}
                          {sub.totalSessions > 0 && (
                            <div style={s.progressWrap}>
                              <div style={s.progressTrack}>
                                <div style={{
                                  ...s.progressFill,
                                  width: `${progress}%`,
                                  background: isExpired ? "#ccc" : accent,
                                }} />
                              </div>
                              <span style={{ ...s.progressPct, color: isExpired ? "#bbb" : accent }}>
                                {progress}%
                              </span>
                            </div>
                          )}
                        </div>

                        {/* ── Meta info ── */}
                        <div style={s.metaGrid}>
                          <MetaItem icon="⏱" label="Duration" value={`${sub.planDurationMinutes} min`} />
                          {fmt(sub.startDate) && (
                            <MetaItem icon="🛒" label="Purchased" value={fmt(sub.startDate)} />
                          )}
                          {sub.endDate && (
                            <MetaItem icon="🏁" label="Ends" value={fmt(sub.endDate)} />
                          )}
                          {sub.validityDays && (
                            <MetaItem icon="📅" label="Validity" value={`${sub.validityDays} days`} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

/* ── Sub-components ── */

const SummaryChip = ({ label, value, color, icon, bg }) => (
  <div style={{ ...s.chip, background: bg }}>
    <span style={s.chipIcon}>{icon}</span>
    <span style={{ ...s.chipValue, color }}>{value}</span>
    <span style={s.chipLabel}>{label}</span>
  </div>
);

const MetaItem = ({ icon, label, value }) => (
  <div style={s.metaItem}>
    <span style={s.metaIcon}>{icon}</span>
    <div>
      <div style={s.metaLabel}>{label}</div>
      <div style={s.metaValue}>{value}</div>
    </div>
  </div>
);

/* ── Styles ── */
const s = {
  page:           { minHeight: "100vh", background: "#f4f6f9" },

  loadingWrap:    { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: 16 },
  spinner:        { width: 36, height: 36, border: "3px solid #e0e0e0", borderTop: "3px solid #028891", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  loadingText:    { color: "#888", fontSize: "0.95rem" },

  pageHeader:     { background: "linear-gradient(135deg, #028891 0%, #046f77 100%)", padding: "32px 20px 28px" },
  headerInner:    { maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 },
  pageTitle:      { margin: 0, fontSize: "1.9rem", fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" },
  pageSubtitle:   { margin: "4px 0 0", fontSize: "0.9rem", color: "rgba(255,255,255,0.75)" },

  btnWrap:        { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
  purchaseBtn:    { padding: "10px 22px", borderRadius: 8, background: "#fff", color: "#028891",
                    border: "none", fontSize: "0.9rem", fontWeight: 700, cursor: "pointer",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)" },
  historyBtn:     { padding: "10px 22px", borderRadius: 8, background: "rgba(255,255,255,0.15)",
                    color: "#fff", border: "1px solid rgba(255,255,255,0.4)", fontSize: "0.9rem",
                    fontWeight: 500, cursor: "pointer" },

  container:      { maxWidth: 1100, margin: "0 auto", padding: "28px 20px 48px" },

  emptyState:     { textAlign: "center", padding: "80px 20px", background: "#fff", borderRadius: 16,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)" },
  emptyIcon:      { fontSize: "3rem", marginBottom: 12 },
  emptyTitle:     { fontSize: "1.3rem", fontWeight: 700, color: "#333", margin: "0 0 8px" },
  emptyText:      { color: "#888", marginBottom: 24 },

  summaryBar:     { background: "#fff", borderRadius: 16, padding: "20px 24px", marginBottom: 32,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)" },
  summaryTitle:   { fontSize: "0.75rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase",
                    letterSpacing: "0.08em", marginBottom: 14 },
  summaryChips:   { display: "flex", flexWrap: "wrap", gap: 12 },
  chip:           { display: "flex", flexDirection: "column", alignItems: "center", minWidth: 100,
                    borderRadius: 12, padding: "12px 20px", gap: 2 },
  chipIcon:       { fontSize: "1.1rem" },
  chipValue:      { fontSize: "1.8rem", fontWeight: 800, lineHeight: 1.1 },
  chipLabel:      { fontSize: "0.7rem", color: "#888", textAlign: "center", marginTop: 2 },

  section:        { marginBottom: 36 },
  sectionHeader:  { display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" },
  sectionIcon:    { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: "1.2rem", flexShrink: 0 },
  sectionTitle:   { fontSize: "1rem", fontWeight: 700 },
  sectionDesc:    { fontSize: "0.75rem", color: "#999", marginTop: 1 },
  sectionCount:   { marginLeft: "auto", fontSize: "0.75rem", fontWeight: 700, padding: "4px 12px",
                    borderRadius: 20 },

  grid:           { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 },

  card:           { background: "#fff", borderRadius: 16, overflow: "hidden",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)", transition: "box-shadow 0.2s ease",
                    display: "flex", flexDirection: "column" },
  cardExpired:    { background: "#fafafa", boxShadow: "none", border: "1px solid #eee" },
  cardAccentBar:  { height: 4, flexShrink: 0 },

  expiredBanner:  { background: "#f5f5f5", color: "#bbb", fontSize: "0.72rem", fontWeight: 700,
                    textAlign: "center", padding: "4px 0", letterSpacing: "0.05em" },

  cardHeader:     { display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "14px 16px 0" },
  typeIcon:       { width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: "1.1rem" },
  activePill:     { fontSize: "0.68rem", fontWeight: 700, color: "#fff", padding: "3px 10px",
                    borderRadius: 20 },

  planName:       { fontSize: "0.95rem", fontWeight: 700, padding: "10px 16px 4px", lineHeight: 1.3 },
  comboTag:       { display: "inline-block", fontSize: "0.68rem", fontWeight: 600, border: "1px solid",
                    borderRadius: 6, padding: "2px 8px", margin: "0 16px 8px" },

  sessionsBlock:  { margin: "8px 16px 0", borderRadius: 12, padding: "14px 16px" },
  sessionMain:    { display: "flex", alignItems: "baseline", gap: 4, marginBottom: 8 },
  sessionBig:     { fontSize: "2.2rem", fontWeight: 800, lineHeight: 1 },
  sessionOf:      { fontSize: "1rem", color: "#aaa", fontWeight: 500 },
  sessionLabel:   { fontSize: "0.75rem", color: "#aaa", marginLeft: 4 },
  progressWrap:   { display: "flex", alignItems: "center", gap: 8 },
  progressTrack:  { flex: 1, height: 6, background: "rgba(0,0,0,0.08)", borderRadius: 4 },
  progressFill:   { height: "100%", borderRadius: 4, transition: "width 0.4s ease" },
  progressPct:    { fontSize: "0.72rem", fontWeight: 700, minWidth: 32, textAlign: "right" },

  metaGrid:       { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
                    padding: "12px 16px 16px", marginTop: 4 },
  metaItem:       { display: "flex", alignItems: "flex-start", gap: 6 },
  metaIcon:       { fontSize: "0.85rem", marginTop: 1 },
  metaLabel:      { fontSize: "0.65rem", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.04em" },
  metaValue:      { fontSize: "0.78rem", fontWeight: 600, color: "#444", marginTop: 1 },
};

export default MyPlansPage;
